import React, { useState, useEffect, useMemo } from 'react';
import illustration from '../../../assets/subscriptionBanner.png';
import css from './Sections.module.css';
import { IconSpinner, Modal } from '../../../components';
import {
  encrypt,
  getDefaultLocale,
  isArrayLength,
  isLocaleGreek,
} from '../../../util/genericHelpers';
import { getProductPriceDetails, getSubscriptionData } from '../../../helpers/dataDupe';
import {
  COOKIE_OPTIONS,
  MIXPANEL_EVENTS,
  recurringIntervalMap,
  StatusCode,
  subscriptionOptions,
} from '../../../util/enums';
import { createCheckoutSession } from '../ProgramOnboardingPage.duck';
import Cookies from 'js-cookie';
import SignupForm from '../../AuthenticationPage/SignupForm/SignupForm';
import { USER_TYPE } from '../../../helpers/enums';
import { authenticationInProgress, signup } from '../../../ducks/auth.duck';
import { useSelector } from 'react-redux';
import TermsAndConditions from '../../AuthenticationPage/TermsAndConditions/TermsAndConditions';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { verifyCodeSelector } from '../../../ducks/global.duck';
import { fetchCurrentUser, selectCurrentUser } from '../../../ducks/user.duck';
import { createStripeFreeSubscription, updateUserSubscriptionData } from '../../../util/api';
import { useConfiguration } from '../../../context/configurationContext';
import { manageDisableScrolling } from '../../../ducks/ui.duck';
import { buildVoucherFields, deriveVoucherFields } from '../../../util/voucherHelpers';
import {
  couponApplicabilityTypes,
  isCouponApplicableToInterval,
  resolveSignupPricing,
} from '../../../util/signupPricing';
import { trackRegisterEvent } from '../../../util/mixpanelHelpers';
import ConsumerTermsEnglish from '../../TermsOfServicePage/Consumer/ConsumerTermsEnglish';
import ConsumerTermsGreek from '../../TermsOfServicePage/Consumer/ConsumerTermsGreek';
import PrivacyEnglish from '../../PrivacyPolicyPage/PrivacyEnglish';
import PrivacyGreek from '../../PrivacyPolicyPage/PrivacyGreek';
import { resolveCheckoutTrialConfig } from '../../../util/trialCheckout';
import {
  getDefaultWebSubscriptionPlan,
  getVisibleWebSubscriptionPlans,
  shouldForceWebAnnualPlan,
} from '../../../util/webSubscriptionVisibility';

const COUPON_INPUT_ID = 'signup-voucher-input';
const FORCED_CORPORATE_MONTHLY_PRICE_IDS = Object.freeze({
  production: 'price_1T4OWBE2qaJ9PPF7FZw6InLi',
  development: 'price_1T4OWiE2qaJ9PPF7XF5g2ZrI',
});

const getForcedCorporateMonthlyPriceId = () =>
  process.env.REACT_APP_ENV === 'production'
    ? FORCED_CORPORATE_MONTHLY_PRICE_IDS.production
    : FORCED_CORPORATE_MONTHLY_PRICE_IDS.development;

const createForcedCorporateMonthlyPlan = () => ({
  id: getForcedCorporateMonthlyPriceId(),
  recurring_interval: subscriptionOptions.month,
  currency: 'eur',
  unit_amount: 1,
  lookupKey: 'vivianlab_1month',
  productBreakdown: 'VivianLab 1 Month Subscription',
  status: true,
});

const errorSources = Object.freeze({
  AUTH: 'auth',
  PAYMENT: 'payment',
});

const createFlowError = ({ source, message, cause } = {}) => {
  const error = new Error(message || '');
  error.source = source || null;
  if (cause) {
    error.cause = cause;
  }
  return error;
};

const isNonEmptyString = value => typeof value === 'string' && value.trim().length > 0;

const extractErrorMessage = error => {
  if (!error) return null;
  if (isNonEmptyString(error)) return error.trim();

  const candidates = [
    error?.message,
    error?.error,
    error?.title,
    error?.response?.data?.error,
    error?.response?.data?.message,
    error?.response?.data?.title,
    error?.data?.error,
    error?.data?.message,
    error?.cause?.message,
    error?.cause?.error,
    error?.cause?.response?.data?.error,
    error?.cause?.response?.data?.message,
  ];
  const firstText = candidates.find(isNonEmptyString);
  if (firstText) return firstText.trim();

  const apiError = error?.apiErrors?.[0] || error?.cause?.apiErrors?.[0];
  if (isNonEmptyString(apiError?.title)) return apiError.title.trim();
  if (isNonEmptyString(apiError?.detail)) return apiError.detail.trim();
  if (isNonEmptyString(apiError?.meta?.stripeMessage)) return apiError.meta.stripeMessage.trim();

  return null;
};

const isSignupEmailTaken = error => {
  const apiErrors = error?.apiErrors;
  if (!Array.isArray(apiErrors)) return false;
  return apiErrors.some(apiError => apiError?.code === 'email-taken');
};

const resolveFallbackFreeSubscriptionPlan = productPrices => {
  const prices = Array.isArray(productPrices) ? productPrices.filter(Boolean) : [];
  return (
    prices.find(p => p.recurring_interval === subscriptionOptions.year) ||
    prices.find(p => p.recurring_interval === subscriptionOptions.month) ||
    prices[0] ||
    null
  );
};

const ProgramSection = ({
  intl,
  productPrices,
  fetchProductPriceInProgress,
  stripePromise,
  dispatch,
  handleCancel,
  viewport,
  setOpenSubscriptionModal,
  forceCorporateCoupon = false,
}) => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const planParam = query.get('plan');
  const trialRequestedFromPlan = planParam === 'free_trial';

  const [subscriptionUserType, setSubscriptionUserType] = useState(
    forceCorporateCoupon ? 'corporate' : 'regular'
  );
  const [plan, setPlan] = useState(null);
  const [paymentInProgress, setPaymentInProgress] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [discountedPrice, setDiscountedPrice] = useState(null);
  const [activeLegalDoc, setActiveLegalDoc] = useState(null);

  const authInProgress = useSelector(authenticationInProgress);
  const currentUser = useSelector(selectCurrentUser);
  const currentSubscription = getSubscriptionData(currentUser);

  const {
    success: voucherSuccess,
    error: voucherError,
    coupon,
    promotionCodeId,
    isCodeValid,
    couponCode,
  } = useSelector(verifyCodeSelector) || {};
  const couponValidated = Boolean(voucherSuccess && isCodeValid);

  const config = useConfiguration();

  useEffect(() => {
    if (forceCorporateCoupon && subscriptionUserType !== 'corporate') {
      setSubscriptionUserType('corporate');
    }
  }, [forceCorporateCoupon, subscriptionUserType]);

  const annualPlan = productPrices?.find(p => p.recurring_interval === subscriptionOptions.year);
  const productMonthlyPlan = productPrices?.find(
    p => p.recurring_interval === subscriptionOptions.month
  );
  const forcedCorporateMonthlyPlan = useMemo(createForcedCorporateMonthlyPlan, []);
  const monthlyPlan =
    productMonthlyPlan || (forceCorporateCoupon ? forcedCorporateMonthlyPlan : null);
  const corporateDisplayPlan = forceCorporateCoupon ? monthlyPlan : annualPlan;
  const displaySubscriptionUserType = forceCorporateCoupon ? 'corporate' : subscriptionUserType;
  const preferredPlanInterval = useMemo(() => {
    switch (planParam) {
      case 'one_month':
        return subscriptionOptions.month;
      case 'one_year':
        return subscriptionOptions.year;
      case 'free_trial':
        return subscriptionOptions.trial;
      default:
        return null;
    }
  }, [planParam]);

  const visibilityDecision = useMemo(() => {
    const visibilityArgs = {
      productPrices,
      currentSubscription,
      coupon,
      couponCode,
      annualCouponCodes: config?.stripeAnnualCoupons || [],
      couponValidated,
      isCorporateUser: subscriptionUserType === 'corporate' && !forceCorporateCoupon,
    };

    return {
      forceAnnualOnly: shouldForceWebAnnualPlan(visibilityArgs),
      visiblePlans: getVisibleWebSubscriptionPlans(visibilityArgs),
      defaultPlan: getDefaultWebSubscriptionPlan({
        ...visibilityArgs,
        preferredInterval: forceCorporateCoupon
          ? subscriptionOptions.month
          : preferredPlanInterval,
      }),
    };
  }, [
    productPrices,
    currentSubscription,
    coupon,
    couponCode,
    config?.stripeAnnualCoupons,
    couponValidated,
    subscriptionUserType,
    forceCorporateCoupon,
    preferredPlanInterval,
  ]);

  const pricingDecision = useMemo(
    () =>
      resolveSignupPricing({
        plan,
        coupon,
        couponCode,
        annualCouponCodes: config?.stripeAnnualCoupons || [],
        couponValidated,
      }),
    [plan, coupon, couponCode, config?.stripeAnnualCoupons, couponValidated]
  );

  useEffect(() => {
    const nextPlan = visibilityDecision.defaultPlan;
    const visiblePlans = visibilityDecision.visiblePlans;
    const hasVisiblePlan = plan ? visiblePlans.some(item => item.id === plan.id) : false;
    const shouldRespectPlanParam =
      Boolean(preferredPlanInterval) && plan?.id !== nextPlan?.id && nextPlan;

    if (!nextPlan) {
      if (plan) {
        setPlan(null);
      }
      return;
    }

    if (
      !plan ||
      !hasVisiblePlan ||
      shouldRespectPlanParam ||
      (visibilityDecision.forceAnnualOnly &&
        plan.recurring_interval !== subscriptionOptions.year)
    ) {
      if (plan?.id !== nextPlan.id) {
        setPlan(nextPlan);
      }
    }
  }, [
    plan,
    preferredPlanInterval,
    visibilityDecision.defaultPlan,
    visibilityDecision.forceAnnualOnly,
    visibilityDecision.visiblePlans,
  ]);

  useEffect(() => {
    if (
      couponValidated &&
      !forceCorporateCoupon &&
      pricingDecision.enforcedPlanInterval === subscriptionOptions.year &&
      annualPlan &&
      plan?.id !== annualPlan.id
    ) {
      setPlan(annualPlan);
    }
  }, [
    couponValidated,
    forceCorporateCoupon,
    pricingDecision.enforcedPlanInterval,
    annualPlan,
    plan?.id,
  ]);

  // Compute a "coupon vs selected plan" mismatch message
  const couponPlanError = useMemo(() => {
    if (!(voucherSuccess && isCodeValid && coupon && plan)) return null;
    if (pricingDecision.couponAppliesToPlan) return null;
    if (
      pricingDecision.annualOnly ||
      pricingDecision.couponApplicability === couponApplicabilityTypes.ANNUAL
    )
      return 'This coupon is only applicable to the Annual Plan';
    if (pricingDecision.couponApplicability === couponApplicabilityTypes.MONTHLY)
      return 'This coupon is only applicable to the Monthly Plan';
    return null;
  }, [
    voucherSuccess,
    isCodeValid,
    coupon,
    plan,
    pricingDecision.couponAppliesToPlan,
    pricingDecision.couponApplicability,
    pricingDecision.annualOnly,
  ]);

  // Discount preview (right column/corporate)
  useEffect(() => {
    if (typeof pricingDecision.discountedUnitAmount === 'number') {
      setDiscountedPrice(pricingDecision.discountedUnitAmount);
    } else {
      setDiscountedPrice(typeof plan?.unit_amount === 'number' ? plan.unit_amount : null);
    }
  }, [pricingDecision.discountedUnitAmount, plan?.unit_amount]);

  const getPlanLabel = interval => {
    switch (interval) {
      case 'month':
        return intl.formatMessage({ id: 'ProgramOnboardingPage.payMonthly' });
      case 'year':
        return intl.formatMessage({ id: 'ProgramOnboardingPage.payAnnual' });
      case '7_day':
        return intl.formatMessage({ id: 'ProgramOnboardingPage.freeTrial' });
      default:
        return interval;
    }
  };

  const resolveSubmitErrorMessage = err => {
    const extractedMessage = extractErrorMessage(err);
    if (isNonEmptyString(extractedMessage)) return extractedMessage;

    if (err?.source === errorSources.AUTH) {
      return intl.formatMessage({ id: 'ProgramOnboardingPage.authError' });
    }

    if (err?.source === errorSources.PAYMENT) {
      return intl.formatMessage({ id: 'ProgramOnboardingPage.paymentError' });
    }

    return intl.formatMessage({ id: 'ProgramOnboardingPage.genericError' });
  };

  const handleStripeCheckout = async values => {
    const {
      fname: firstName,
      lname: lastName,
      email,
      password,
      dateOfBirth,
      city,
      country,
      preferredLang: preferredLanguage,
      voucher,
      subscriptionType,
    } = values;

    try {
      setPaymentInProgress(true);
      setErrorMessage(null);

      const verifyState = {
        success: voucherSuccess,
        error: voucherError,
        isCodeValid,
        couponId: coupon?.id || null,
        couponCode: couponCode || null,
        promotionCodeId: promotionCodeId || null,
      };
      const voucherState = deriveVoucherFields({
        voucherInput: voucher || couponCode || null,
        verifyState,
      });
      const voucherFields = buildVoucherFields({
        voucher: voucherState.voucher,
        couponCode: voucherState.couponCode,
        couponId: voucherState.couponId,
      });

      const effectiveSubscriptionType = forceCorporateCoupon
        ? 'corporate'
        : subscriptionType || subscriptionUserType;

      if (effectiveSubscriptionType === 'corporate' && !(voucherSuccess && isCodeValid)) {
        throw new Error(
          intl.formatMessage({ id: 'ProgramOnboardingPage.errorCorporateCouponRequired' })
        );
      }

      const billingPlan =
        effectiveSubscriptionType === 'corporate' && forceCorporateCoupon ? monthlyPlan : plan;

      if (!billingPlan)
        throw new Error(
          effectiveSubscriptionType === 'corporate' && forceCorporateCoupon
            ? intl.formatMessage({ id: 'ProgramOnboardingPage.noPrices' })
            : intl.formatMessage({ id: 'ProgramOnboardingPage.errorMissingPlan' })
        );

      // Block if coupon verified but not applicable to the selected plan
      if (
        voucherState.voucher &&
        voucherSuccess &&
        coupon &&
        billingPlan &&
        !isCouponApplicableToInterval({
          coupon,
          interval: billingPlan.recurring_interval,
          couponCode,
          annualCouponCodes: config?.stripeAnnualCoupons || [],
        })
      ) {
        if (
          pricingDecision.annualOnly ||
          pricingDecision.couponApplicability === couponApplicabilityTypes.ANNUAL
        )
          throw new Error('This coupon is only applicable to the Annual Plan');
        if (pricingDecision.couponApplicability === couponApplicabilityTypes.MONTHLY)
          throw new Error('This coupon is only applicable to the Monthly Plan');
      }

      const currentPricingDecision = resolveSignupPricing({
        plan: billingPlan,
        coupon,
        couponCode,
        annualCouponCodes: config?.stripeAnnualCoupons || [],
        couponValidated: Boolean(voucherState.isVerified),
      });

      const freeSignupPath =
        voucherState.isVerified &&
        currentPricingDecision.couponAppliesToPlan &&
        !currentPricingDecision.requiresPaymentMethod;

      if (freeSignupPath) {
        const freePlan =
          currentPricingDecision.enforcedPlanInterval === subscriptionOptions.year
            ? annualPlan || billingPlan
            : billingPlan || annualPlan;
        const fallbackFreePlan = freePlan || resolveFallbackFreeSubscriptionPlan(productPrices);
        const freeSubscriptionPriceId =
          fallbackFreePlan?.id || process.env.REACT_APP_FREE_SUBSCRIPTION_PRICE_ID || null;

        trackRegisterEvent(MIXPANEL_EVENTS.COUPON_FREE_SIGNUP, {
          coupon: voucherState.couponCode || voucherState.voucher,
          planInterval:
            fallbackFreePlan?.recurring_interval ||
            currentPricingDecision.enforcedPlanInterval ||
            subscriptionOptions.year,
          flow: 'register',
        });

        let freeSubResp;
        try {
          freeSubResp = await createStripeFreeSubscription({
            email,
            priceId: freeSubscriptionPriceId,
            promotionCodeId: voucherState.promotionCodeId || null,
            couponId: !voucherState.promotionCodeId ? voucherState.couponId || null : null,
            metadata: {
              firstName,
              lastName,
              source: 'ProgramSection',
            },
          });
        } catch (paymentError) {
          throw createFlowError({
            source: errorSources.PAYMENT,
            cause: paymentError,
          });
        }

        if (freeSubResp?.status !== 'success') {
          throw createFlowError({
            source: errorSources.PAYMENT,
            message:
              freeSubResp?.error ||
              intl.formatMessage({ id: 'ProgramOnboardingPage.paymentError' }),
          });
        }

        const isCorporateCouponUser =
          effectiveSubscriptionType === 'corporate' || Boolean(voucherFields.voucher);

        const signupParams = {
          email,
          password,
          firstName,
          lastName,
          protectedData: {
            dob: dateOfBirth,
            city,
            country,
            preferredLang: preferredLanguage || getDefaultLocale(),
            ...voucherFields,
            subscriptionType: isCorporateCouponUser ? 'corporate' : 'regular',
          },
          privateData: {
            hasTrialStarted: true,
            trialCreationDate: new Date().toISOString(),
            trialExpirationDate: new Date(
              new Date().setDate(new Date().getDate() + (voucherFields.voucher ? 365 * 5 : 0))
            ).toISOString(),
            stripeCustomerId: freeSubResp.customerId,
            stripeSubscriptionId: freeSubResp.subscriptionId,
            isCorporateUser: isCorporateCouponUser,
          },
        };

        const signupData = await dispatch(signup(signupParams));
        const signupSucceeded = signupData?.statusCode === StatusCode.SUCCESS;
        if (!signupSucceeded) {
          if (isSignupEmailTaken(signupData?.error)) {
            throw createFlowError({
              source: errorSources.AUTH,
              message: intl.formatMessage({
                id: 'AuthenticationPage.signupFailedEmailAlreadyTaken',
              }),
              cause: signupData?.error,
            });
          }

          throw createFlowError({
            source: errorSources.AUTH,
            cause: signupData?.error || signupData,
          });
        }

        try {
          await updateUserSubscriptionData({
            subscriptionId: freeSubResp.subscriptionId,
            email,
            protectedData: {
              ...voucherFields,
              subscriptionType: isCorporateCouponUser ? 'corporate' : 'regular',
            },
          });
          await dispatch(fetchCurrentUser());
        } catch (updateError) {
          console.error('Failed to sync free signup subscription data:', updateError);
        }

        setOpenSubscriptionModal(true);
        return;
      }

      trackRegisterEvent(MIXPANEL_EVENTS.REQUIRES_PAYMENT, {
        coupon: voucherState.couponCode || voucherState.voucher,
        planInterval: billingPlan.recurring_interval,
        flow: 'register',
      });

      const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY;
      if (!encryptionKey) {
        throw new Error(
          intl.formatMessage({ id: 'ProgramOnboardingPage.errorMissingEncryptionKey' })
        );
      }

      const setEncryptedCookie = (key, plainText) => {
        const encrypted = encrypt(plainText, encryptionKey);
        Cookies.set(key, encrypted, COOKIE_OPTIONS);
      };

      setEncryptedCookie('p_key', password);
      setEncryptedCookie('e_key', email);
      Cookies.set('f_key', firstName, COOKIE_OPTIONS);
      Cookies.set('l_key', lastName, COOKIE_OPTIONS);
      Cookies.set('d_key', dateOfBirth, COOKIE_OPTIONS);
      Cookies.set('ci_key', city, COOKIE_OPTIONS);
      Cookies.set('co_key', country, COOKIE_OPTIONS);
      Cookies.set('pr_key', preferredLanguage, COOKIE_OPTIONS);
      Cookies.set('st_key', effectiveSubscriptionType, COOKIE_OPTIONS);

      if (voucherFields.voucher && voucherState.isVerified) {
        Cookies.set('vou_key', voucherFields.voucher, COOKIE_OPTIONS);
        Cookies.set('cc_key', voucherFields.couponCode || voucherFields.voucher, COOKIE_OPTIONS);
        if (voucherFields.couponId) {
          Cookies.set('cid_key', voucherFields.couponId, COOKIE_OPTIONS);
        } else {
          Cookies.remove('cid_key');
        }
      } else if (voucherError) {
        Cookies.remove('vou_key');
        Cookies.remove('cc_key');
        Cookies.remove('cid_key');
      }

      const { productBreakdown } = getProductPriceDetails(billingPlan) || {};
      const trialConfig = resolveCheckoutTrialConfig({
        entryPoint: 'register_flow',
        plan: billingPlan,
        trialRequested: trialRequestedFromPlan,
      });
      const payload = {
        email,
        price: billingPlan.id,
        currency: billingPlan.currency,
        unit_amount: billingPlan.unit_amount,
        priceName: productBreakdown || 'Product',
        interval: billingPlan.recurring_interval,
        promotionCodeId: voucherState.promotionCodeId || null,
        couponId: !voucherState.promotionCodeId ? voucherState.couponId || null : null,
        enableTrial: trialConfig.enableTrial,
        trialDays: trialConfig.trialDays,
        fromPath: location.pathname?.replace(/^\/+/, '') || 'register',
      };

      console.info('[Checkout] register-flow-trial-config', {
        entryPoint: 'register_flow',
        selectedPlanId: billingPlan?.id || null,
        selectedInterval: trialConfig.planInterval,
        trialRequestedFromPlan,
        trialFlow: trialConfig.trialFlow,
        trialDays: trialConfig.trialDays,
      });

      const session = await dispatch(createCheckoutSession(payload));
      const sessionId = session?.id || session?.data?.id || null;
      if (!sessionId) {
        throw createFlowError({
          source: errorSources.PAYMENT,
          message: intl.formatMessage({ id: 'ProgramOnboardingPage.errorCreateSession' }),
        });
      }

      const stripe = await stripePromise;
      if (!stripe?.redirectToCheckout) {
        throw createFlowError({
          source: errorSources.PAYMENT,
          message: intl.formatMessage({ id: 'ProgramOnboardingPage.paymentError' }),
        });
      }
      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) {
        throw createFlowError({
          source: errorSources.PAYMENT,
          cause: error,
        });
      }
    } catch (err) {
      setErrorMessage(resolveSubmitErrorMessage(err));
    } finally {
      setPaymentInProgress(false);
    }
  };

  const isSubscribeDisabled =
    paymentInProgress ||
    (displaySubscriptionUserType !== 'corporate' && !plan) ||
    (displaySubscriptionUserType === 'corporate' && (!voucherSuccess || !isCodeValid));

  const closeLegalModal = () => {
    setActiveLegalDoc(null);
  };

  const legalModalTitle =
    activeLegalDoc === 'terms'
      ? intl.formatMessage({ id: 'TermsOfServicePage.schemaTitle' })
      : intl.formatMessage({ id: 'PrivacyPolicyPage.schemaTitle' });

  const legalContent =
    activeLegalDoc === 'terms'
      ? isLocaleGreek()
        ? <ConsumerTermsGreek />
        : <ConsumerTermsEnglish />
      : isLocaleGreek()
      ? <PrivacyGreek />
      : <PrivacyEnglish />;

  const visibleProductPrices = visibilityDecision.visiblePlans;

  const scrollToCoupon = () => {
    const el =
      document.getElementById(COUPON_INPUT_ID) || document.querySelector('input[name="voucher"]');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      try {
        el.focus({ preventScroll: true });
      } catch (_) {}
      if (css.highlightPulse) {
        el.classList.add(css.highlightPulse);
        window.setTimeout(() => el.classList.remove(css.highlightPulse), 1600);
      }
      el.style.width = '100%';
      el.style.boxSizing = 'border-box';
    }
  };

  return (
    <div className={css.container}>
      <div className={css.left}>
        <div className={css.formSection}>
          <SignupForm
            userType={USER_TYPE.INDIVIDUAL}
            className={css.signupForm}
            onSubmit={handleStripeCheckout}
            inProgress={authInProgress}
            viewport={viewport}
            hideUserType
            isProgram
            termsAndConditions={
              <TermsAndConditions
                intl={intl}
                isCustomer
                onOpenTermsOfService={() => setActiveLegalDoc('terms')}
                onOpenPrivacyPolicy={() => setActiveLegalDoc('privacy')}
              />
            }
            handleCancel={handleCancel}
            isSubscribeDisabled={isSubscribeDisabled}
            paymentInProgress={paymentInProgress}
            subscriptionUserType={displaySubscriptionUserType}
            onSubscriptionTypeChange={setSubscriptionUserType}
            productPrices={productPrices}
            plan={plan}
            setPlan={setPlan}
            couponInputId={COUPON_INPUT_ID}
            couponPlanError={couponPlanError} // pass red mismatch text to field
            forceCorporateCoupon={forceCorporateCoupon}
          />
          {errorMessage ? <div className={css.errorBanner}>{errorMessage}</div> : null}
        </div>
      </div>

      <div className={css.right}>
        {displaySubscriptionUserType === 'regular' && (
          <div className={css.planHead}>
            <h2 className={css.plansTitle}>
              {intl.formatMessage({ id: 'ProgramOnboardingPage.availablePlans' })}
            </h2>
            {fetchProductPriceInProgress ? (
              <IconSpinner />
            ) : (
              <div className={css.planOptions}>
                {isArrayLength(visibleProductPrices) ? (
                  visibleProductPrices.map((price, index) => {
                    const isSelected = plan?.id === price.id;
                    const trialBadgeConfig = resolveCheckoutTrialConfig({
                      entryPoint: 'register_flow',
                      plan: price,
                    });

                    // Show discount only when coupon applies to THIS interval
                    const priceDecision = resolveSignupPricing({
                      plan: price,
                      coupon,
                      couponCode,
                      annualCouponCodes: config?.stripeAnnualCoupons || [],
                      couponValidated: Boolean(voucherSuccess && isCodeValid),
                    });
                    const showDiscount =
                      isSelected &&
                      voucherSuccess &&
                      coupon &&
                      price.unit_amount > 0 &&
                      priceDecision.couponAppliesToPlan;
                    const displayAmount =
                      typeof priceDecision.discountedUnitAmount === 'number'
                        ? priceDecision.discountedUnitAmount
                        : price.unit_amount;

                    return (
                      <label
                        key={price.id}
                        className={`${css.planOption} ${isSelected ? css.selected : ''}`}
                        onClick={() => setPlan(price)}
                      >
                        <input
                          type="radio"
                          name="plan"
                          value={price.id}
                          checked={isSelected}
                          onChange={() => setPlan(price)}
                          className={css.hiddenRadio}
                        />
                        <span className={css.customRadio} />
                        <div className={css.planInfo}>
                          <span className={css.planName}>
                            {getPlanLabel(price.recurring_interval)}
                          </span>
                          <span className={css.planPrice}>
                            {showDiscount && displayAmount !== price.unit_amount ? (
                              <>
                                <span className={css.originalPrice}>
                                  {intl.formatNumber(price.unit_amount / 100, {
                                    style: 'currency',
                                    currency: price.currency.toUpperCase(),
                                  })}
                                </span>{' '}
                                {intl.formatNumber(displayAmount / 100, {
                                  style: 'currency',
                                  currency: price.currency.toUpperCase(),
                                })}
                              </>
                            ) : (
                              // Non-applicable coupon → only original price (no discounted 5.77 etc.)
                              intl.formatNumber(price.unit_amount / 100, {
                                style: 'currency',
                                currency: price.currency.toUpperCase(),
                              })
                            )}
                            {index > 0 && (
                              <>
                                {' / '}
                                {recurringIntervalMap[getDefaultLocale()]?.[
                                  price.recurring_interval
                                ] || intl.formatMessage({ id: 'ProgramOnboardingPage.loc' })}
                              </>
                            )}
                          </span>
                        </div>

                        {trialBadgeConfig.enableTrial && !pricingDecision.isFree && (
                          <span className={css.badge}>
                            {intl.formatMessage({ id: 'ProgramOnboardingPage.trialLabel' })}
                          </span>
                        )}
                      </label>
                    );
                  })
                ) : (
                  <span className={css.planName}>
                    {intl.formatMessage({ id: 'ProgramOnboardingPage.noPrices' })}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {displaySubscriptionUserType === 'corporate' && (
          <div className={css.planHead}>
            <div className={css.corporateInfo}>
              <h2 className={css.corporateTitle}>
                {intl.formatMessage({ id: 'ProgramOnboardingPage.corporateSubscription' })}
              </h2>

              <div className={css.corporateDescription}>
                <p className={css.corporateParagraph}>
                  {intl.formatMessage({ id: 'ProgramOnboardingPage.corporateDescription' })}
                </p>

                {!voucherSuccess && (
                  <button
                    type="button"
                    className={css.corporateInstruction}
                    onClick={scrollToCoupon}
                    aria-label={intl.formatMessage({
                      id: 'ProgramOnboardingPage.enterCorporateCoupon',
                    })}
                  >
                    {intl.formatMessage({ id: 'ProgramOnboardingPage.enterCorporateCoupon' })}
                  </button>
                )}

                {voucherSuccess && corporateDisplayPlan && (
                  <div className={css.couponEffect}>
                    <h3 className={css.subHeading}>
                      {intl.formatMessage({ id: 'ProgramOnboardingPage.yourSubscription' })}
                    </h3>

                    <div className={css.priceDisplay}>
                      <span className={css.subscriptionType}>
                        {getPlanLabel(corporateDisplayPlan.recurring_interval)}
                      </span>

                      {discountedPrice !== null && (
                        <span className={css.discountedPrice}>
                          {discountedPrice === 0 ? (
                            <span className={css.freeText}>
                              {intl.formatMessage({ id: 'ProgramOnboardingPage.free' })}
                            </span>
                          ) : (
                            <>
                              {discountedPrice !== corporateDisplayPlan.unit_amount && (
                                <span className={css.originalCorporatePrice}>
                                  {intl.formatNumber(corporateDisplayPlan.unit_amount / 100, {
                                    style: 'currency',
                                    currency: corporateDisplayPlan.currency.toUpperCase(),
                                  })}
                                </span>
                              )}{' '}
                              <span className={css.finalPrice}>
                                {intl.formatNumber(discountedPrice / 100, {
                                  style: 'currency',
                                  currency: corporateDisplayPlan.currency.toUpperCase(),
                                })}
                                {' / '}
                                {recurringIntervalMap[getDefaultLocale()]?.[
                                  corporateDisplayPlan.recurring_interval
                                ] || intl.formatMessage({ id: 'ProgramOnboardingPage.loc' })}
                              </span>
                            </>
                          )}
                        </span>
                      )}
                    </div>

                    {coupon?.percent_off ? (
                      <div className={css.discountInfo}>
                        {intl.formatMessage(
                          { id: 'ProgramOnboardingPage.discountApplied' },
                          { discount: coupon.percent_off }
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <img src={illustration} alt="" className={css.illustration} />
      </div>

      <Modal
        id="ProgramOnboardingPage.legalDocument"
        isOpen={Boolean(activeLegalDoc)}
        onClose={closeLegalModal}
        usePortal
        closeOnBackdropClick
        trapFocus
        restoreFocusOnClose
        containerClassName={css.legalModalContainer}
        modalTitle={legalModalTitle}
        contentClassName={css.legalModalBody}
        onManageDisableScrolling={(componentId, disableScrolling) =>
          dispatch(manageDisableScrolling(componentId, disableScrolling))
        }
      >
        <div className={css.legalDocumentContent}>{legalContent}</div>
      </Modal>
    </div>
  );
};

export default ProgramSection;
